import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Button,
  Avatar,
  Heading,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaSmile,
  FaMeh,
  FaFrown,
  FaChevronRight,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getReviews } from '../services/reviewService';
import { getYearData } from '../services/yearService';
import { getBusinessImages } from '../services/imageService';
import { logger } from '../utils/logger';

const MotionBox = motion(Box);

/**
 * SentimentAnalysisWidget - Full-width widget displaying market sentiment analysis
 * Features market sentiment score, distribution bar, and top 3 performers podium
 */
const SentimentAnalysisWidget = () => {
  const navigate = useNavigate();
  const [reviewsData, setReviewsData] = useState(null);
  const [yearData, setYearData] = useState([]);
  const [businessImages, setBusinessImages] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Color mode values - all hooks must be called unconditionally at top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const firstPlaceBg = useColorModeValue('yellow.50', 'gray.700');

  // Sentiment colors
  const SENTIMENT_COLORS = {
    positive: useColorModeValue('#48BB78', '#68D391'),
    neutral: useColorModeValue('#ECC94B', '#F6E05E'),
    negative: useColorModeValue('#E53E3E', '#FC8181')
  };

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        // Fetch reviews and year data in parallel
        const [reviewsResponse, yearDataResponse] = await Promise.all([
          getReviews(),
          getYearData()
        ]);

        if (mounted) {
          setReviewsData(reviewsResponse);
          setYearData(yearDataResponse);
        }

        // Fetch business images
        const images = await getBusinessImages().catch(err => {
          logger.log('Images not available:', err.message);
          return [];
        });

        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching Sentiment Analysis data:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Combine reviews data with year data to get sentiment breakdown
  const combinedData = useMemo(() => {
    if (!reviewsData || !yearData) return [];

    return reviewsData.map((currentData) => {
      const historicalData = yearData.find(
        (yd) => yd.maps_uri === currentData.maps_uri
      );

      return {
        ...currentData,
        historicalData: historicalData?.data || null
      };
    });
  }, [reviewsData, yearData]);

  // Calculate sentiment metrics for each business
  const businessSentimentData = useMemo(() => {
    return combinedData.map((business) => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0
      };

      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0
          ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) / total) * 100
          : 0;

      const positivePercentage = total > 0 ? (sentimentBreakdown.positive / total) * 100 : 0;

      return {
        ...business,
        sentimentScore,
        positivePercentage,
        ...sentimentBreakdown,
        total
      };
    });
  }, [combinedData]);

  // Sort businesses by sentiment score
  const sortedBySentiment = useMemo(() => {
    if (!businessSentimentData.length) return [];
    return [...businessSentimentData].sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [businessSentimentData]);

  // Top 3 for podium
  const top3Sentiment = useMemo(() => sortedBySentiment.slice(0, 3), [sortedBySentiment]);

  // Market-wide metrics
  const marketMetrics = useMemo(() => {
    const totalPositive = sortedBySentiment.reduce((sum, b) => sum + b.positive, 0);
    const totalNeutral = sortedBySentiment.reduce((sum, b) => sum + b.neutral, 0);
    const totalNegative = sortedBySentiment.reduce((sum, b) => sum + b.negative, 0);
    const totalReviews = totalPositive + totalNeutral + totalNegative;

    const marketSentimentScore =
      totalReviews > 0
        ? ((totalPositive + totalNeutral * 0.5) / totalReviews) * 100
        : 0;

    return {
      totalPositive,
      totalNeutral,
      totalNegative,
      totalReviews,
      marketSentimentScore,
      positivePercentage: totalReviews > 0 ? (totalPositive / totalReviews) * 100 : 0,
      neutralPercentage: totalReviews > 0 ? (totalNeutral / totalReviews) * 100 : 0,
      negativePercentage: totalReviews > 0 ? (totalNegative / totalReviews) * 100 : 0
    };
  }, [sortedBySentiment]);

  const handleViewDetails = () => {
    navigate('/dashboards/sentiment');
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <Flex align="center" justify="center" minH="200px">
          <VStack spacing={3}>
            <Spinner size="lg" color="green.500" thickness="3px" />
            <Text color={subtitleColor} fontSize="sm">Loading sentiment data...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  // No data state
  if (!reviewsData || sortedBySentiment.length === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <VStack spacing={3} py={8}>
          <Icon as={FaSmile} boxSize={10} color="gray.300" />
          <Text color={subtitleColor}>No sentiment data available</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="sm"
      _hover={{ shadow: 'md', borderColor: 'green.200' }}
      position="relative"
    >
      {/* Gradient accent bar */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="4px"
        bgGradient="linear(to-r, green.400, teal.400, blue.400)"
      />

      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        p={4}
        pb={3}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <HStack spacing={3}>
          <Icon as={FaSmile} color="green.500" boxSize={5} />
          <VStack align="start" spacing={0}>
            <Heading size="md" color={textColor} textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
              Sentiment Analysis
            </Heading>
            <Text fontSize="xs" color={subtitleColor}>
              Customer satisfaction across {sortedBySentiment.length} businesses
            </Text>
          </VStack>
        </HStack>
        <Button
          size="sm"
          colorScheme="green"
          variant="ghost"
          rightIcon={<FaChevronRight />}
          onClick={handleViewDetails}
        >
          View Details
        </Button>
      </Flex>

      {/* Main Content */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} p={4}>
        {/* Left: Market Sentiment Score */}
        <Box
          bg={statBg}
          p={5}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <Text fontWeight="semibold" color={textColor}>Market Sentiment</Text>
              <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} />
            </HStack>
            
            <Stat>
              <StatNumber
                fontSize="4xl"
                fontWeight="bold"
                color={
                  marketMetrics.marketSentimentScore >= 70
                    ? 'green.500'
                    : marketMetrics.marketSentimentScore >= 50
                    ? 'yellow.500'
                    : 'red.500'
                }
              >
                {marketMetrics.marketSentimentScore.toFixed(1)}%
              </StatNumber>
              <StatHelpText mb={0}>
                Based on {marketMetrics.totalReviews.toLocaleString()} reviews
              </StatHelpText>
            </Stat>

            {/* Sentiment Distribution Bar */}
            <Box>
              <Text fontSize="xs" color={subtitleColor} mb={2}>Distribution</Text>
              <Box
                w="100%"
                h="12px"
                bg="gray.200"
                borderRadius="full"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  h="100%"
                  w={`${marketMetrics.positivePercentage}%`}
                  bg={SENTIMENT_COLORS.positive}
                  transition="width 0.5s ease"
                />
                <Box
                  position="absolute"
                  left={`${marketMetrics.positivePercentage}%`}
                  top={0}
                  h="100%"
                  w={`${marketMetrics.neutralPercentage}%`}
                  bg={SENTIMENT_COLORS.neutral}
                  transition="all 0.5s ease"
                />
                <Box
                  position="absolute"
                  left={`${marketMetrics.positivePercentage + marketMetrics.neutralPercentage}%`}
                  top={0}
                  h="100%"
                  w={`${marketMetrics.negativePercentage}%`}
                  bg={SENTIMENT_COLORS.negative}
                  transition="all 0.5s ease"
                />
              </Box>

              {/* Legend */}
              <HStack mt={2} justify="space-between" fontSize="xs">
                <HStack spacing={1}>
                  <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={3} />
                  <Text color={subtitleColor}>{marketMetrics.positivePercentage.toFixed(0)}%</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={FaMeh} color={SENTIMENT_COLORS.neutral} boxSize={3} />
                  <Text color={subtitleColor}>{marketMetrics.neutralPercentage.toFixed(0)}%</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={3} />
                  <Text color={subtitleColor}>{marketMetrics.negativePercentage.toFixed(0)}%</Text>
                </HStack>
              </HStack>
            </Box>
          </VStack>
        </Box>

        {/* Right: Top 3 Podium - spans 2 columns */}
        <Box gridColumn={{ base: 'auto', lg: 'span 2' }}>
          <Text fontWeight="semibold" color={textColor} mb={4}>Top Sentiment Leaders</Text>
          
          <Flex justify="center" align="flex-end" gap={3} w="full">
            {/* Second Place */}
            {top3Sentiment[1] && (
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                flex="1"
                maxW="200px"
                textAlign="center"
                cursor="pointer"
                onClick={handleViewDetails}
                _hover={{ transform: 'translateY(-4px)' }}
                borderRadius="xl"
                bg={statBg}
                p={3}
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="xl" mb={2}>🥈</Text>
                <Avatar
                  name={top3Sentiment[1].name}
                  src={businessImages[top3Sentiment[1].maps_uri] || ''}
                  size="md"
                  mb={2}
                  borderWidth="2px"
                  borderColor="gray.300"
                />
                <Text fontSize="sm" fontWeight="medium" noOfLines={1} mb={1}>
                  {top3Sentiment[1].name}
                </Text>
                <Text
                  fontWeight="bold"
                  fontSize="lg"
                  color={top3Sentiment[1].sentimentScore >= 70 ? 'green.500' : 'yellow.500'}
                >
                  {top3Sentiment[1].sentimentScore.toFixed(1)}%
                </Text>
                <HStack justify="center" spacing={2} fontSize="xs" mt={1}>
                  <HStack spacing={0.5}>
                    <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={2.5} />
                    <Text>{top3Sentiment[1].positive}</Text>
                  </HStack>
                  <HStack spacing={0.5}>
                    <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={2.5} />
                    <Text>{top3Sentiment[1].negative}</Text>
                  </HStack>
                </HStack>
              </MotionBox>
            )}

            {/* First Place */}
            {top3Sentiment[0] && (
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                flex="1.2"
                maxW="240px"
                textAlign="center"
                cursor="pointer"
                onClick={handleViewDetails}
                _hover={{ transform: 'translateY(-6px)' }}
                borderRadius="xl"
                bgGradient="linear(to-b, yellow.50, white)"
                p={4}
                borderWidth="2px"
                borderColor="yellow.300"
                boxShadow="0 4px 15px -3px rgba(251, 191, 36, 0.3)"
                mb={2}
                zIndex={2}
              >
                <Text fontSize="2xl" mb={2}>🏆</Text>
                <Avatar
                  name={top3Sentiment[0].name}
                  src={businessImages[top3Sentiment[0].maps_uri] || ''}
                  size="lg"
                  mb={2}
                  borderWidth="3px"
                  borderColor="yellow.400"
                />
                <Text fontSize="md" fontWeight="bold" noOfLines={1} mb={1}>
                  {top3Sentiment[0].name}
                </Text>
                <Text
                  fontWeight="extrabold"
                  fontSize="2xl"
                  bgGradient="linear(to-r, green.500, teal.500)"
                  bgClip="text"
                >
                  {top3Sentiment[0].sentimentScore.toFixed(1)}%
                </Text>
                <HStack justify="center" spacing={3} fontSize="xs" mt={1}>
                  <HStack spacing={0.5}>
                    <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={3} />
                    <Text fontWeight="medium">{top3Sentiment[0].positive}</Text>
                  </HStack>
                  <HStack spacing={0.5}>
                    <Icon as={FaMeh} color={SENTIMENT_COLORS.neutral} boxSize={3} />
                    <Text fontWeight="medium">{top3Sentiment[0].neutral}</Text>
                  </HStack>
                  <HStack spacing={0.5}>
                    <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={3} />
                    <Text fontWeight="medium">{top3Sentiment[0].negative}</Text>
                  </HStack>
                </HStack>
              </MotionBox>
            )}

            {/* Third Place */}
            {top3Sentiment[2] && (
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                flex="1"
                maxW="200px"
                textAlign="center"
                cursor="pointer"
                onClick={handleViewDetails}
                _hover={{ transform: 'translateY(-4px)' }}
                borderRadius="xl"
                bg={statBg}
                p={3}
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="xl" mb={2}>🥉</Text>
                <Avatar
                  name={top3Sentiment[2].name}
                  src={businessImages[top3Sentiment[2].maps_uri] || ''}
                  size="md"
                  mb={2}
                  borderWidth="2px"
                  borderColor="orange.300"
                />
                <Text fontSize="sm" fontWeight="medium" noOfLines={1} mb={1}>
                  {top3Sentiment[2].name}
                </Text>
                <Text
                  fontWeight="bold"
                  fontSize="lg"
                  color={top3Sentiment[2].sentimentScore >= 70 ? 'green.500' : 'yellow.500'}
                >
                  {top3Sentiment[2].sentimentScore.toFixed(1)}%
                </Text>
                <HStack justify="center" spacing={2} fontSize="xs" mt={1}>
                  <HStack spacing={0.5}>
                    <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={2.5} />
                    <Text>{top3Sentiment[2].positive}</Text>
                  </HStack>
                  <HStack spacing={0.5}>
                    <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={2.5} />
                    <Text>{top3Sentiment[2].negative}</Text>
                  </HStack>
                </HStack>
              </MotionBox>
            )}
          </Flex>
        </Box>
      </SimpleGrid>
    </MotionBox>
  );
};

export default SentimentAnalysisWidget;
